import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Car, Loader2, Trash2, Pencil, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { DocumentsSection } from '@/components/DocumentsSection';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const emptyForm = { client_id: '', brand: '', model: '', year: '', vin: '', plate_number: '', data_dobandire: '', serie_motor: '', capacitate_cilindrica: '' };

export default function AdminVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [expandedVehicle, setExpandedVehicle] = useState(null);

  useEffect(() => { fetchVehicles(); fetchClients(); }, []);

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API + '/admin/vehicles', { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      setVehicles(response.data);
    } catch (error) {
      toast.error('Eroare');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API + '/admin/clients', { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      setClients(response.data);
    } catch (error) {}
  };

  const openCreate = () => { setEditingId(null); setForm({ ...emptyForm }); setDialogOpen(true); };

  const openEdit = (v) => {
    setEditingId(v.vehicle_id);
    setForm({
      client_id: v.client_id || '', brand: v.brand || '', model: v.model || '',
      year: v.year || '', vin: v.vin || '', plate_number: v.plate_number || '',
      data_dobandire: v.data_dobandire || '', serie_motor: v.serie_motor || '',
      capacitate_cilindrica: v.capacitate_cilindrica || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.brand || !form.vin) { toast.error('Completati campurile obligatorii'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const vehicleData = { ...form, year: form.year ? parseInt(form.year) : null, capacitate_cilindrica: form.capacitate_cilindrica ? parseInt(form.capacitate_cilindrica) : null };
      if (editingId) {
        await axios.put(API + '/admin/vehicles/' + editingId, vehicleData, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
        toast.success('Vehicul actualizat');
      } else {
        await axios.post(API + '/admin/vehicles', vehicleData, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
        toast.success('Vehicul adaugat');
      }
      setDialogOpen(false); setForm({ ...emptyForm }); setEditingId(null); fetchVehicles();
    } catch (error) { toast.error('Eroare'); } finally { setSaving(false); }
  };

  const deleteVehicle = async (vehicleId, vehicleName) => {
    if (!confirm(`Sigur doriti sa stergeti vehiculul "${vehicleName}"?`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(API + '/admin/vehicles/' + vehicleId, { headers: { Authorization: 'Bearer ' + token }, withCredentials: true });
      toast.success('Vehicul sters'); fetchVehicles();
    } catch (error) { toast.error(error.response?.data?.detail || 'Eroare la stergere'); }
  };

  const toggleExpand = (vehicleId) => {
    setExpandedVehicle(expandedVehicle === vehicleId ? null : vehicleId);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-semibold text-slate-900">Vehicule</h1><p className="text-slate-500">Gestioneaza vehiculele</p></div>
        <Button data-testid="add-vehicle-btn" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="h-4 w-4 mr-2" />Vehicul nou</Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editeaza vehicul' : 'Adauga vehicul'}</DialogTitle>
            <DialogDescription>{editingId ? 'Modificati datele vehiculului' : 'Completati datele vehiculului nou'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Proprietar *</Label><Select value={form.client_id} onValueChange={(v) => setForm({...form, client_id: v})}><SelectTrigger><SelectValue placeholder="Selecteaza" /></SelectTrigger><SelectContent>{clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-4"><div><Label>Marca *</Label><Input data-testid="vehicle-brand" value={form.brand} onChange={(e) => setForm({...form, brand: e.target.value})} /></div><div><Label>Model</Label><Input data-testid="vehicle-model" value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} /></div></div>
            <div><Label>VIN *</Label><Input data-testid="vehicle-vin" value={form.vin} onChange={(e) => setForm({...form, vin: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nr. inmat.</Label><Input value={form.plate_number} onChange={(e) => setForm({...form, plate_number: e.target.value})} /></div>
              <div><Label>An</Label><Input type="number" value={form.year} onChange={(e) => setForm({...form, year: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Data dobandire</Label><Input type="date" data-testid="vehicle-data-dobandire" value={form.data_dobandire} onChange={(e) => setForm({...form, data_dobandire: e.target.value})} /></div>
              <div><Label>Serie motor</Label><Input data-testid="vehicle-serie-motor" value={form.serie_motor} onChange={(e) => setForm({...form, serie_motor: e.target.value})} placeholder="ex: ABC123456" /></div>
            </div>
            <div><Label>Capacitate cilindrica (cc)</Label><Input type="number" data-testid="vehicle-capacitate" value={form.capacitate_cilindrica} onChange={(e) => setForm({...form, capacitate_cilindrica: e.target.value})} placeholder="ex: 1600" /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Anuleaza</Button><Button type="submit" data-testid="vehicle-save-btn" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? 'Se salveaza...' : 'Salveaza'}</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div> : vehicles.length === 0 ? <Card className="bg-white"><CardContent className="py-12 text-center"><Car className="h-12 w-12 text-slate-300 mx-auto mb-4" /><p>Niciun vehicul</p></CardContent></Card> : (
        <div className="grid gap-4">
          {vehicles.map((v) => (
            <Card key={v.vehicle_id} className="bg-white hover:shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center"><Car className="h-6 w-6 text-emerald-600" /></div>
                    <div>
                      <p className="font-medium">{v.brand} {v.model}</p>
                      <p className="text-sm text-slate-500">VIN: {v.vin}</p>
                      {v.capacitate_cilindrica && <p className="text-sm text-slate-500">Capacitate: {v.capacitate_cilindrica} cc</p>}
                      {v.client && <Link to={`/admin/clients/${v.client.client_id}`} className="text-sm text-blue-600 hover:underline">Proprietar: {v.client.name}</Link>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {v.plate_number && <Badge variant="secondary">{v.plate_number}</Badge>}
                    {(v.documents?.length || 0) > 0 && <Badge variant="outline" className="text-blue-600">{v.documents.length} doc</Badge>}
                    <Button variant="outline" size="sm" onClick={() => toggleExpand(v.vehicle_id)} data-testid={`expand-vehicle-${v.vehicle_id}`}>
                      {expandedVehicle === v.vehicle_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                    <Link to={`/admin/vehicles/${v.vehicle_id}`}>
                      <Button variant="outline" size="sm" data-testid={`view-vehicle-${v.vehicle_id}`} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button data-testid={`edit-vehicle-${v.vehicle_id}`} variant="outline" size="sm" onClick={() => openEdit(v)} className="text-slate-600 hover:text-slate-700">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => deleteVehicle(v.vehicle_id, `${v.brand} ${v.model}`)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {expandedVehicle === v.vehicle_id && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <DocumentsSection
                      documents={v.documents || []}
                      uploadUrl={`/admin/vehicles/${v.vehicle_id}/documents`}
                      onRefresh={fetchVehicles}
                      title="Documente vehicul"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
